import { getDiscreteAvailabilitySummary, type TDiscreteAvailabilitySummary } from "./availabilityDiscrete";
import { getCycleRecapAndCycleAvailabilities, type IHosAvailability, type IHosPreciseAvailability, type IState, msToTimespan } from "./helpers";
import { getDailyDutyAvailabilityForCurrentDay, getWorkdayRestAvailabilityForBetweenRestLimit } from "./availabilityHelpers";
import { setExemptionValues } from "./exemptionHelpers";
import { applyEmergencyResponseFloor } from "./exemptions/emergencyResponse";

const getMinNumber = (values: (number | undefined)[]): number | undefined => {
    let numbers = values.filter((x): x is number => typeof x === "number");
    return numbers.length === 0 ? undefined : Math.min(...numbers);
};

export const getWorkdayAlternateRestAvailability = (state: IState) => {
    let earlyRestBreakConfig = state.config.rest?.earlyRestBreaks;
    if (!earlyRestBreakConfig || earlyRestBreakConfig.length === 0) {
        return undefined;
    }
    // todo: shouldn't this get the earlyConfig with the min/max betweenRestLimit, not the first one it finds?
    let rest: number | undefined = undefined,
        label: string | undefined = undefined;
    for (let i = 0; i < earlyRestBreakConfig.length; i++) {
        let timeUntilMustRest = getWorkdayRestAvailabilityForBetweenRestLimit(state, earlyRestBreakConfig[i].betweenRestLimit);
        if (timeUntilMustRest && timeUntilMustRest > 0) {
            rest = timeUntilMustRest;
            label = msToTimespan(earlyRestBreakConfig[i].restBreakMinimum);
            break;
        }
    }
    return { rest, label };
};

const getAggregateDutyAvailabilityForUI = (
    discreteAvailabilitySummary: TDiscreteAvailabilitySummary,
    dailyDutyAvailability: number | undefined
): number | undefined => {
    if (discreteAvailabilitySummary.Duty === undefined) {
        return undefined;
    }
    let limiters = [discreteAvailabilitySummary.Duty, discreteAvailabilitySummary.DutySinceCycleRest, dailyDutyAvailability];
    let result = getMinNumber(limiters);
    return result === undefined ? undefined : Math.max(0, result);
};

const getAggregateDrivingAvailabilityForUI = (
    discreteAvailabilitySummary: TDiscreteAvailabilitySummary,
    dutyAvailability: number | undefined,
    cycleAvailability: number | undefined
): number | undefined => {
    if (discreteAvailabilitySummary.Driving === undefined) {
        return undefined;
    }
    let limiters = [
        discreteAvailabilitySummary.Driving,
        discreteAvailabilitySummary.Workday,
        discreteAvailabilitySummary.DailyDriving,
        discreteAvailabilitySummary.CycleDriving,
        dutyAvailability,
        cycleAvailability
    ];
    let result = getMinNumber(limiters);
    return result === undefined ? undefined : Math.max(0, result);
};

const getAggregateCycleAvailabilityForUI = (
    cycleAvailability: number | undefined,
    cycleRestAvailability: number | undefined,
    cycleEndAvailability: number | undefined
): number | undefined => {
    let limiters = [cycleAvailability, cycleRestAvailability, cycleEndAvailability];
    let result = getMinNumber(limiters);
    return result === undefined ? undefined : Math.max(0, result);
};

// todo: why does alternateRest always take priority over the default rest? Shouldn't we take the availability with more?
const getAggregateRestAvailabilityAndLabel = (
    state: IState,
    discreteAvailabilitySummary: TDiscreteAvailabilitySummary
): { rest: number | undefined; restLabel?: string | undefined } => {
    if (!state.config.rest?.earlyRestBreaks) {
        // 99% of rulesets exit here
        return { rest: discreteAvailabilitySummary.Rest };
    }
    let alternateRestAvailability = getWorkdayAlternateRestAvailability(state);
    return alternateRestAvailability?.rest === undefined
        ? { rest: discreteAvailabilitySummary.Rest, restLabel: msToTimespan(state.config.rest.restBreakMinimum) }
        : { rest: alternateRestAvailability.rest, restLabel: alternateRestAvailability.label };
};

interface IAggregateAvailabilities {
    duty: number | undefined;
    driving: number | undefined;
    cycle: number | undefined;
    rest: number | undefined;
    restLabel: string | undefined;
}

const getAllAggregateAvailabilitiesForUI = (state: IState, discreteAvailabilitySummary: TDiscreteAvailabilitySummary): IAggregateAvailabilities => {
    let duty = getAggregateDutyAvailabilityForUI(discreteAvailabilitySummary, getDailyDutyAvailabilityForCurrentDay(state));
    let cycle = getAggregateCycleAvailabilityForUI(discreteAvailabilitySummary.Cycle, discreteAvailabilitySummary.CycleRest, discreteAvailabilitySummary.CycleEnd);
    let driving = getAggregateDrivingAvailabilityForUI(discreteAvailabilitySummary, duty, cycle);
    let { rest, restLabel } = getAggregateRestAvailabilityAndLabel(state, discreteAvailabilitySummary);
    return { duty, driving, cycle, rest, restLabel };
};

const getPreciseAvailabilitySummaryForUI = (state: IState): IHosPreciseAvailability => {
    let discreteAvailabilitySummary = getDiscreteAvailabilitySummary(state, true);
    let result: IHosPreciseAvailability = {
        cycleRest: discreteAvailabilitySummary.CycleRest,
        dutySinceCycleRest: discreteAvailabilitySummary.DutySinceCycleRest,
        workday: discreteAvailabilitySummary.Workday,
        cycleDriving: discreteAvailabilitySummary.CycleDriving,
        dailyOff: discreteAvailabilitySummary.DailyOff,
        dailyDriving: discreteAvailabilitySummary.DailyDriving,
        dailyDuty: discreteAvailabilitySummary.DailyDuty
    };

    let aggregateAvailabilities = getAllAggregateAvailabilitiesForUI(state, discreteAvailabilitySummary);
    result.duty = aggregateAvailabilities.duty;
    result.rest = aggregateAvailabilities.rest;
    result.restLabel = aggregateAvailabilities.restLabel;
    result.driving = aggregateAvailabilities.driving;
    if (state.config.cycle?.cycleMaximum || state.config.cycle?.cycleDrivingMaximum) {
        // todo: should probably move the ruleset config checks into the discrete availability functions
        // todo: why do we check the rulesetConfig for cycleDrivingMax when cycleDriving availability is not used to calculate cycle availability?
        // todo: why does the result affect the driving availability when the ruleset config does not have cycleMax or cycleDrivingMax?
        result.cycle = aggregateAvailabilities.cycle;
    }

    // MYG-139401: the discrete pass already floored everything derived from the summary; this
    // catches the two limiters that bypass it — getDailyDutyAvailabilityForCurrentDay and the
    // alternate rest availability. Idempotent, so applying it twice is harmless.
    applyEmergencyResponseFloor(state, result);

    (Object.keys(result) as (keyof IHosPreciseAvailability)[]).forEach(key => result[key] === undefined && delete result[key]);
    return result;
};

export const calculateAvailability = (
    state: IState,
    getAllRecaps = false
): { availability: IHosAvailability; preciseAvailability: IHosPreciseAvailability } => {
    let { hosRecap, hosCycleAvailabilities } = getCycleRecapAndCycleAvailabilities(state, getAllRecaps);
    let availability: IHosAvailability = { recap: hosRecap };
    if (hosCycleAvailabilities !== undefined) {
        availability.cycleAvailabilities = hosCycleAvailabilities;
    }
    let preciseAvailability = getPreciseAvailabilitySummaryForUI(state);
    if (state.config.rest?.onDutyCountTowardRest && state.workday.consecutiveRestCount !== undefined) {
        preciseAvailability.drivingBreakDuration = state.workday.consecutiveRestCount;
    }
    setExemptionValues(state, availability);
    for (let key in preciseAvailability) {
        let preciseKey = key as keyof IHosPreciseAvailability;
        let value = preciseAvailability[preciseKey];
        if (value !== undefined) {
            availability[preciseKey] = typeof value === "number" ? msToTimespan(value) : value;
        }
    }

    return { availability, preciseAvailability };
};
